import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSiteSettings } from "@/contexts/SettingsContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Circle, Lock, CreditCard, ShieldCheck, Banknote, ArrowRight, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import IdentityVerificationCard from "@/components/IdentityVerificationCard";
import StripeConnectCard from "@/components/StripeConnectCard";

const formatCad = (n) => {
  const v = typeof n === "number" ? n : parseFloat(n);
  if (!isFinite(v)) return "10";
  return Number.isInteger(v) ? String(v) : v.toFixed(2).replace(".", ",");
};
const formatFeePct = (rate) => {
  const v = typeof rate === "number" ? rate : parseFloat(rate);
  if (!isFinite(v)) return "3";
  const pct = v * 100;
  return (Number.isInteger(pct) ? String(pct) : pct.toFixed(2).replace(/\.?0+$/, "")).replace(".", ",");
};

/**
 * 3-step host onboarding wizard:
 * 1. Subscription (10$/year)
 * 2. Identity verification (3 photos)
 * 3. Stripe Connect (Express account)
 */
export default function HostOnboardingPage() {
  const { user } = useAuth();
  const { settings } = useSiteSettings();
  const feePctLabel = formatFeePct(settings?.platform_fee_rate);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState({ status: "inactive", price: 10 });
  const [verification, setVerification] = useState(null);
  const [stripeStatus, setStripeStatus] = useState({ connected: false });
  const [subscribing, setSubscribing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [sub, me, conn] = await Promise.all([
        api.get("/subscription/status").then(r => r.data),
        api.get("/auth/me").then(r => r.data),
        api.get("/connect/status").then(r => r.data),
      ]);
      setSubscription(sub);
      setVerification(me.identity_verification || { status: "unverified" });
      setStripeStatus(conn);
    } catch (e) {
      toast.error("Erreur de chargement");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!user) return;
    load();

    // Handle Stripe redirect after subscription
    const sessionId = searchParams.get("session_id");
    const subSuccess = searchParams.get("subscription");
    if (subSuccess === "success" && sessionId) {
      api.get(`/subscription/verify/${sessionId}`).then(() => {
        toast.success("Abonnement activé !");
        load();
      });
      const next = new URLSearchParams(searchParams);
      next.delete("subscription");
      next.delete("session_id");
      setSearchParams(next, { replace: true });
    }
    if (searchParams.get("stripe") === "onboarded") {
      toast.success("Compte Stripe configuré !");
      const next = new URLSearchParams(searchParams);
      next.delete("stripe");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const subscribe = async () => {
    setSubscribing(true);
    try {
      const { data } = await api.post("/subscription/checkout", { origin_url: `${window.location.origin}/host-onboarding` });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else if (data.free_mode) {
        toast.success(data.message || "Abonnement activé (mode test)");
        load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
      setSubscribing(false);
    }
  };

  // Compute step statuses
  const step1Done = subscription.status === "active";
  const step2Done = verification?.status === "verified";
  const step3Done = stripeStatus.connected && stripeStatus.charges_enabled && stripeStatus.payouts_enabled;
  const allDone = step1Done && step2Done && step3Done;

  // Determine which step is "current" (first non-done)
  let currentStep = 1;
  if (step1Done && !step2Done) currentStep = 2;
  if (step1Done && step2Done && !step3Done) currentStep = 3;
  if (allDone) currentStep = 4;

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12">
      {/* Header */}
      <div className="mb-8">
        <Badge className="bg-emerald-100 text-emerald-700 border-0 mb-3">Devenir hôte</Badge>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-zinc-950 mb-2" data-testid="onboarding-title">
          {allDone ? "Tout est prêt !" : "Configurez votre profil d'hôte"}
        </h1>
        <p className="text-zinc-600">
          {allDone
            ? "Vous pouvez maintenant publier votre premier terrain et recevoir des réservations."
            : "3 étapes simples pour commencer à louer votre terrain. Comptez 10-15 min au total."}
        </p>
      </div>

      {/* Progress bar */}
      <div className="flex items-center gap-2 mb-8" data-testid="progress-bar">
        {[1, 2, 3].map((n) => {
          const done = (n === 1 && step1Done) || (n === 2 && step2Done) || (n === 3 && step3Done);
          const current = n === currentStep;
          return (
            <div key={n} className="flex-1 flex items-center gap-2">
              <div className={`flex items-center justify-center h-8 w-8 rounded-full text-sm font-bold shrink-0 ${
                done ? "bg-emerald-600 text-white" : current ? "bg-zinc-950 text-white" : "bg-zinc-200 text-zinc-500"
              }`}>
                {done ? <CheckCircle className="h-4 w-4" /> : n}
              </div>
              {n < 3 && <div className={`flex-1 h-0.5 ${done ? "bg-emerald-600" : "bg-zinc-200"}`} />}
            </div>
          );
        })}
      </div>

      {/* All done state */}
      {allDone && (
        <Card className="border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white rounded-2xl mb-6" data-testid="onboarding-complete">
          <CardContent className="p-6 sm:p-8 text-center">
            <Sparkles className="h-12 w-12 text-emerald-600 mx-auto mb-3" />
            <h2 className="font-heading text-2xl font-bold text-zinc-950 mb-2">Bravo, votre compte est prêt !</h2>
            <p className="text-zinc-600 mb-6">Vous pouvez maintenant publier votre premier terrain et accueillir des campeurs.</p>
            <Button onClick={() => navigate("/create-space")} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-11 px-6" data-testid="goto-create-space-btn">
              <ArrowRight className="h-4 w-4 mr-2" /> Publier mon premier terrain
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step 1: Subscription */}
      <Step
        n={1}
        icon={CreditCard}
        title="Abonnement annuel"
        description={subscription.price > 0
          ? `${formatCad(subscription.price)} $ par an pour publier vos terrains sans limites`
          : "Abonnement gratuit — activation immédiate pour publier vos terrains sans limites"}
        done={step1Done}
        current={currentStep === 1}
        locked={false}
        data-testid="step-1"
      >
        {step1Done ? (
          <div className="flex items-center gap-2 text-sm text-emerald-700">
            <CheckCircle className="h-4 w-4" />
            Abonnement actif jusqu'au <strong>{subscription.expires ? new Date(subscription.expires).toLocaleDateString("fr-CA") : "N/A"}</strong>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="bg-zinc-50 rounded-xl p-4 border border-zinc-200">
              <div className="flex items-baseline gap-2 mb-3">
                {subscription.price > 0 ? (
                  <>
                    <span className="text-3xl font-bold text-zinc-950">{formatCad(subscription.price)} $</span>
                    <span className="text-sm text-zinc-500">/ an</span>
                  </>
                ) : (
                  <span className="text-3xl font-bold text-emerald-700">Gratuit</span>
                )}
              </div>
              <ul className="text-sm text-zinc-700 space-y-1.5">
                <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-emerald-600 shrink-0" /> Publication illimitée de terrains</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-emerald-600 shrink-0" /> Messagerie avec les campeurs</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-emerald-600 shrink-0" /> Calendrier et statistiques</li>
                <li className="flex items-center gap-2 text-xs text-zinc-500 mt-2">− Commission de {feePctLabel}% déduite de chaque réservation</li>
              </ul>
            </div>
            <Button onClick={subscribe} disabled={subscribing} className="w-full sm:w-auto bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl" data-testid="onboarding-subscribe-btn">
              {subscribing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />}
              {subscription.price > 0 ? `S'abonner pour ${formatCad(subscription.price)} $` : "Activer mon abonnement gratuit"}
            </Button>
          </div>
        )}
      </Step>

      {/* Step 2: Identity */}
      <Step
        n={2}
        icon={ShieldCheck}
        title="Vérification d'identité"
        description="3 photos de votre permis de conduire pour la sécurité de la plateforme"
        done={step2Done}
        current={currentStep === 2}
        locked={!step1Done}
        data-testid="step-2"
      >
        {!step1Done ? (
          <p className="text-sm text-zinc-500 italic">Terminez l'étape 1 pour débloquer cette étape.</p>
        ) : (
          <IdentityVerificationCard verification={verification} onUpdated={(v) => { setVerification(v); load(); }} />
        )}
      </Step>

      {/* Step 3: Stripe */}
      <Step
        n={3}
        icon={Banknote}
        title="Connecter mon compte Stripe"
        description="Pour recevoir vos paiements automatiquement après chaque séjour"
        done={step3Done}
        current={currentStep === 3}
        locked={!step1Done || !step2Done}
        data-testid="step-3"
      >
        {!step1Done || !step2Done ? (
          <p className="text-sm text-zinc-500 italic">
            Terminez les étapes précédentes pour débloquer cette étape.
          </p>
        ) : (
          <StripeConnectCard />
        )}
      </Step>
    </div>
  );
}

function Step({ n, icon: Icon, title, description, done, current, locked, children, ...rest }) {
  return (
    <Card className={`mb-4 rounded-2xl transition-all ${
      done ? "border-emerald-200 bg-emerald-50/30" :
      current ? "border-zinc-950 border-2 shadow-sm" :
      locked ? "border-zinc-200 opacity-60" :
      "border-zinc-200"
    }`} {...rest}>
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${
            done ? "bg-emerald-100 text-emerald-700" : locked ? "bg-zinc-100 text-zinc-400" : "bg-zinc-950 text-white"
          }`}>
            {locked ? <Lock className="h-4 w-4" /> : done ? <CheckCircle className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-heading text-lg font-bold text-zinc-950">Étape {n} — {title}</h3>
              {done && <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">Complété</Badge>}
              {current && !done && <Badge className="bg-zinc-950 text-white border-0 text-xs">En cours</Badge>}
            </div>
            <p className="text-sm text-zinc-500 mt-0.5">{description}</p>
          </div>
        </div>
        <div className={locked ? "pl-13" : ""}>{children}</div>
      </CardContent>
    </Card>
  );
}
